export interface Environment {
  id: string
  apiEndpoint: string
  linrestApiGateway: string
  region: string
  city: string
  flag: string
  enabled: boolean
  tier: 'prod' | 'dev'
}

export const ENVIRONMENTS: Record<string, Environment> = {
  prod: {
    id: 'prod',
    apiEndpoint: 'https://api.linbo.in',
    linrestApiGateway: 'https://api.linearb.io',
    region: 'us-east-2',
    city: 'Ohio',
    flag: '🇺🇸',
    enabled: true,
    tier: 'prod'
  },
  'prod-eu': {
    id: 'prod-eu',
    apiEndpoint:
      'https://45mkh8jujj.execute-api.eu-central-1.amazonaws.com/prod-eu',
    linrestApiGateway: 'https://api.linearb-eu.io',
    region: 'eu-central-1',
    city: 'Frankfurt',
    flag: '🇪🇺',
    enabled: true,
    tier: 'prod'
  },
  'dev-01': {
    id: 'dev-01',
    apiEndpoint: 'https://api.linbo-test.in',
    linrestApiGateway: 'https://api.linearb-dev-01.io',
    region: 'eu-central-1',
    city: 'Frankfurt',
    flag: '🇩🇪',
    enabled: true,
    tier: 'dev'
  },
  'dev-02': {
    id: 'dev-02',
    apiEndpoint:
      'https://k4i4dlkh73.execute-api.eu-west-1.amazonaws.com/dev-02',
    linrestApiGateway: 'https://api.linearb-dev-02.io',
    region: 'eu-west-1',
    city: 'Ireland',
    flag: '🇮🇪',
    enabled: true,
    tier: 'dev'
  },
  'qa-01': {
    id: 'qa-01',
    apiEndpoint:
      'https://{REPLACE-WITH-API-ID}.execute-api.us-east-1.amazonaws.com/qa-01',
    linrestApiGateway: 'https://api.linearb-qa-01.io',
    region: 'us-east-1',
    city: 'N. Virginia',
    flag: '🇺🇸',
    enabled: false,
    tier: 'dev'
  },
  'staging-01': {
    id: 'staging-01',
    apiEndpoint:
      'https://{REPLACE-WITH-API-ID}.execute-api.us-east-1.amazonaws.com/staging-01',
    linrestApiGateway: 'https://api.linearb-staging-01.io',
    region: 'us-east-1',
    city: 'N. Virginia',
    flag: '🇺🇸',
    enabled: false,
    tier: 'dev'
  }
}

export const DEFAULT_ENVIRONMENT = 'dev-01'

// localStorage key for selected environment
export const SELECTED_ENVIRONMENT_KEY = 'selectedEnvironment'

export const findEnvironmentByApiEndpoint = (
  apiEndpoint: string
): Environment | undefined =>
  Object.values(ENVIRONMENTS).find(
    environment => environment.apiEndpoint === apiEndpoint
  )

// The environment this build targets, or undefined for localhost
const BUILD_ENVIRONMENT = findEnvironmentByApiEndpoint(
  import.meta.env.VITE_API_ENDPOINT_URL
)

// Get current environment from localStorage or default; a selection from the other tier is ignored
export const getCurrentEnvironment = (): Environment => {
  const selected = ENVIRONMENTS[localStorage.getItem(SELECTED_ENVIRONMENT_KEY)]

  if (
    selected &&
    (!BUILD_ENVIRONMENT || selected.tier === BUILD_ENVIRONMENT.tier)
  ) {
    return selected
  }

  return BUILD_ENVIRONMENT ?? ENVIRONMENTS[DEFAULT_ENVIRONMENT]
}

// The same page in another environment: ids differ per region, so an /organization/:id
// style page falls back to its list, and a page the target region doesn't register is
// caught by the router's catch-all
export const pathForEnvironmentSwitch = (): string => {
  const [section, ...rest] = window.location.pathname.split('/').filter(Boolean)
  return rest.length > 0
    ? `/${section}`
    : window.location.pathname + window.location.search
}

export const setEnvironment = (envId: string): void => {
  if (!ENVIRONMENTS[envId]) {
    console.error(`Invalid environment: ${envId}`)
    return
  }

  localStorage.setItem(SELECTED_ENVIRONMENT_KEY, envId)
  window.location.replace(pathForEnvironmentSwitch())
}
